﻿<template>
  <!--
    One option of a drawing picker, drawn with the family's *real* artwork.

    The picker used to show the Material Symbol that came with the option, which
    said "a heat pump" in the same way for every kind â€” the whole point of the
    choice is the shape, so each option renders the drawing the tile will show.
    The state each preview runs in is chosen to show the machine working: water in
    a running pump, brine in a borehole, hot water in a cylinder, a cover half
    down, a live plug.
  -->
  <span class="variant-drawing">
    <PumpAnimation v-if="drawing === 'pump'" :kind="kind as PumpKind" active :speed="70" />
    <LightAnimation v-else-if="drawing === 'light'" :kind="kind as LightKind" active :level="80" />
    <FanAnimation v-else-if="drawing === 'fan'" :kind="kind as FanKind" active />
    <TankAnimation v-else-if="drawing === 'tank'" :kind="kind as TankKind" :level="62" />
    <HeatPumpAnimation v-else-if="drawing === 'heat_pump'" :kind="kind as HeatPumpKind" active mode="heat" />
    <CoverAnimation v-else-if="drawing === 'cover'" :kind="kind as CoverKind" :position="55" :tilt="45" />
    <SensorAnimation v-else-if="drawing === 'reading'" :kind="kind as SensorKind" :value="62" />
    <SwitchAnimation v-else-if="drawing === 'appliance'" :kind="kind as SwitchKind" active :level="80" />
    <BinarySensorAnimation v-else-if="drawing === 'binary_sensor'" :kind="kind as BinarySensorKind" active />
    <LockAnimation v-else-if="drawing === 'lock'" :kind="kind as LockKind" :locked="true" />
    <!-- A family this build does not draw still gets a tile: its own glyph. -->
    <span v-else class="material-symbols-outlined variant-drawing__glyph">{{ icon || 'category' }}</span>
  </span>
</template>

<script setup lang="ts">
import BinarySensorAnimation from '../devices/binary-sensor/BinarySensorAnimation.vue';
import CoverAnimation from '../devices/cover/CoverAnimation.vue';
import FanAnimation from '../devices/fan/FanAnimation.vue';
import HeatPumpAnimation from '../devices/heat-pump/HeatPumpAnimation.vue';
import LockAnimation from '../devices/lock/LockAnimation.vue';
import LightAnimation from '../devices/light/LightAnimation.vue';
import PumpAnimation from '../devices/pump/PumpAnimation.vue';
import SensorAnimation from '../devices/sensor/SensorAnimation.vue';
import SwitchAnimation from '../devices/switch/SwitchAnimation.vue';
import TankAnimation from '../devices/tank/TankAnimation.vue';
import type {
  BinarySensorKind,
  CoverKind,
  FanKind,
  HeatPumpKind,
  LightKind,
  LockKind,
  PumpKind,
  SensorKind,
  SwitchKind,
  TankKind,
} from '../devices/deviceWidgetTypes';

// The value IS the drawing's id, so the narrowings in the template are the
// contract the hub's option list and each sketchbook already share.
defineProps<{
  /** The family's `drawing` id â€” which sketchbook to draw from. */
  drawing: string;
  /** The option's value, which is the drawing's own id. */
  kind: string;
  /** The option's Material Symbol, shown only when this build has no drawing. */
  icon?: string;
}>();
</script>

<style scoped>
.variant-drawing {
  display: block;
  width: 100%;
  height: 100%;
  /* The drawings are line art in `currentColor`, and the gray scale is re-themed
     per theme (theme-black's `--gray-600` is nearly black, which drew black line
     art on a black tile) â€” so the ink comes from the slate pair instead. */
  color: rgb(71 85 105);
}

.variant-drawing__glyph {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  font-size: 1.5rem;
}

.dark .variant-drawing {
  color: rgb(203 213 225);
}
</style>
